import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiMail, FiChevronDown } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { LuBookX } from 'react-icons/lu';
import { HiArrowsRightLeft } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import ATable from '../../organisms/ATable';
import CallRegistryModalForm from '../communication/contact/CallRegistryModalForm';
import SelectTextField from '../../molecules/SelectTextField';
import appConfig from '../../../config/app-config';
import { collateralApi } from '../../../services/collateralApi';
import { formatCurrency, formatDate, formatCpfCnpj } from './collateralUtils';

const COMM_API = appConfig.apis.communicationApiEnv;

const STATUS_LABELS: Record<string, string> = {
    NOT_STARTED: 'Não iniciado', IN_VALIDATION: 'Em validação',
    FULLY_VALIDATED: 'Validado', PARTIALLY_VALIDATED: 'Validado parcial',
    PENDING: 'Pendente', CONFIRMED: 'Confirmado', NOT_CONFIRMED: 'Não confirmado',
};
const STATUS_COLORS: Record<string, string> = {
    NOT_STARTED: 'bg-gray-100 text-gray-600', IN_VALIDATION: 'bg-yellow-100 text-yellow-700',
    FULLY_VALIDATED: 'bg-green-100 text-green-700', PARTIALLY_VALIDATED: 'bg-orange-100 text-orange-700',
    PENDING: 'bg-gray-100 text-gray-600', CONFIRMED: 'bg-green-100 text-green-700', NOT_CONFIRMED: 'bg-red-100 text-red-700',
};
const HISTORY_LABELS: Record<string, string> = {
    CALL: 'Ligação registrada', EMAIL: 'Envio de email', WHATSAPP: 'Envio de WhatsApp',
    VALIDATED: 'Validação realizada', CONFIRMED: 'Confirmado', NOT_CONFIRMED: 'Não confirmado', UNMARKED: 'Desmarcado',
};

const CONTACT_STATUSES: { value: string; label: string; color: string }[] = [
    { value: 'NONE', label: 'Não iniciado', color: 'bg-gray-500' },
    { value: 'FAST', label: 'Rápido', color: 'bg-green-500' },
    { value: 'WAITING', label: 'Aguardando retorno', color: 'bg-purple-500' },
    { value: 'MODERATELY_FAST', label: 'Moderadamente rápido', color: 'bg-yellow-500' },
    { value: 'LONG_TIME', label: 'Demora responder', color: 'bg-orange-500' },
    { value: 'NO_RESPOND', label: 'Não responde', color: 'bg-red-500' },
];

interface Props {
    show: boolean;
    onClose: () => void;
    analysisId: string;
    contactId?: string;
    debtorName: string;
    debtorDocument: string;
    debtorStatus?: string;
    contactStatus?: string;
    receivables: any[];
    operationCode: string;
    history?: any[];
    onRefresh?: () => void;
}

const DraweeValidationPageForm = ({ show, onClose, analysisId, contactId, debtorName, debtorDocument, debtorStatus, contactStatus: initialContactStatus, receivables: initialReceivables, operationCode, history: initialHistory, onRefresh }: Props) => {
    const [contact, setContact] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [showCallForm, setShowCallForm] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [showFarolMenu, setShowFarolMenu] = useState(false);
    const [farolStatus, setFarolStatus] = useState(initialContactStatus || 'NONE');
    const [showActionMenu, setShowActionMenu] = useState<string | null>(null);
    const [notConfirmId, setNotConfirmId] = useState<string | null>(null);
    const [notConfirmObs, setNotConfirmObs] = useState('');
    const [notConfirmReason, setNotConfirmReason] = useState('');

    const NOT_CONFIRM_REASONS = ['Divergência de data', 'Divergência de valor', 'Sacado não reconhece dívida', 'Outro motivo'];

    const handleReasonChange = (reason: string) => {
        setNotConfirmReason(reason);
        if (reason !== 'Outro motivo') setNotConfirmObs(reason);
        else setNotConfirmObs('');
    };
    const [receivables, setReceivables] = useState(initialReceivables);
    const [history, setHistory] = useState(initialHistory || []);
    const [dvStatus, setDvStatus] = useState(debtorStatus || 'NOT_STARTED');

    useEffect(() => {
        setReceivables(initialReceivables);
        setHistory(initialHistory || []);
        setDvStatus(debtorStatus || 'NOT_STARTED');
        setFarolStatus(initialContactStatus || 'NONE');
    }, [debtorDocument, initialHistory?.length]);

    const isValidated = dvStatus === 'FULLY_VALIDATED' || dvStatus === 'PARTIALLY_VALIDATED';

    const totals = useMemo(() => {
        const confirmed = receivables.filter(r => r.status === 'CONFIRMED');
        const notConfirmed = receivables.filter(r => r.status === 'NOT_CONFIRMED');
        const pending = receivables.filter(r => r.status === 'PENDING');
        return {
            count: receivables.length,
            amount: receivables.reduce((s: number, r: any) => s + (r.amount || 0), 0),
            confirmedCount: confirmed.length,
            confirmedAmount: confirmed.reduce((s: number, r: any) => s + (r.amount || 0), 0),
            notConfirmedCount: notConfirmed.length,
            pendingCount: pending.length,
        };
    }, [receivables]);

    useEffect(() => {
        if (!show) return;
        setContact(null);
        if (!contactId && !debtorDocument) return;

        setLoading(true);
        (async () => {
            try {
                const { Auth } = await import('aws-amplify');
                const session = await Auth.currentSession();
                const token = session.getIdToken().getJwtToken();
                const headers = { 'x-api-key': COMM_API.key || '', 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

                let matchId = contactId;
                if (!matchId) {
                    const digits = debtorDocument.replace(/\D/g, '');
                    if (digits.length >= 3) {
                        const searchRes = await fetch(`${COMM_API.address}/contacts/search?q=${encodeURIComponent(digits)}`, { headers });
                        const searchData = await searchRes.json();
                        const match = searchData.items?.find((c: any) => c.taxId?.replace(/\D/g, '') === digits);
                        if (match) {
                            matchId = match.id;
                        }
                    }
                }
                if (!matchId) { setLoading(false); return; }

                const res = await fetch(`${COMM_API.address}/contacts/${matchId}/page-view`, { headers });
                const d = await res.json();
                const phones = (d.contactPoints || []).filter((cp: any) => (cp.type === 'PHONE' || cp.type === 'WHATSAPP') && cp.isActive).map((cp: any) => ({ id: cp.id, number: cp.valueFormatted, whatsapp: cp.type === 'WHATSAPP' }));
                const emails = (d.contactPoints || []).filter((cp: any) => cp.type === 'EMAIL' && cp.isActive).map((cp: any) => ({ id: cp.id, address: cp.valueFormatted }));
                const contactData = { id: d.id, name: d.fullName, status: d.status || 'NONE', phones, emails };
                setContact(contactData);
                setFarolStatus(d.status || 'NONE');
                // Auto-link contact to collateral drawee (with farol status)
                if (!contactId || d.status !== initialContactStatus) collateralApi.linkContact(analysisId, debtorDocument, d.id, d.status || 'NONE').then(() => onRefresh?.()).catch(() => {});
            } catch { setContact(null); }
            finally { setLoading(false); }
        })();
    }, [show, contactId, debtorDocument]);

    const deals = useMemo(() => receivables.map(r => ({
        id: r.id, operation: operationCode, number: r.titleNumber || r.receivableNumber, assignor: debtorName, value: String(r.amount), status: r.status,
    })), [receivables, operationCode, debtorName]);

    const handleCreateContact = useCallback(async () => {
        setProcessing(true);
        try {
            const { Auth } = await import('aws-amplify');
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const headers = { 'x-api-key': COMM_API.key || '', 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
            const digits = debtorDocument.replace(/\D/g, '');
            const contactType = digits.length >= 14 ? 'LEGAL_PERSON' : 'INDIVIDUAL';
            const res = await fetch(`${COMM_API.address}/contacts`, {
                method: 'POST', headers,
                body: JSON.stringify({ taxId: digits, fullName: debtorName, contactType, status: 'NONE' }),
            });
            if (res.ok) {
                const d = await res.json();
                const contactData = { id: d.id, name: debtorName, status: 'NONE', phones: [], emails: [] };
                setContact(contactData);
                // Link contact to collateral drawee
                await collateralApi.linkContact(analysisId, debtorDocument, d.id, 'NONE').catch(() => {});
                toast.success('Contato criado e vinculado');
            } else { toast.error('Erro ao criar contato'); }
        } catch { toast.error('Erro ao criar contato'); }
        finally { setProcessing(false); }
    }, [debtorDocument, debtorName]);

    const handleFarolChange = useCallback(async (newStatus: string) => {
        setShowFarolMenu(false);
        if (!contact) return;
        try {
            const { Auth } = await import('aws-amplify');
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            const res = await fetch(`${COMM_API.address}/contacts/${contact.id}`, {
                method: 'PUT',
                headers: { 'x-api-key': COMM_API.key || '', 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus }),
            });
            if (!res.ok) throw new Error('Failed to update status');
            setFarolStatus(newStatus);
            await collateralApi.linkContact(analysisId, debtorDocument, contact.id, newStatus).catch(() => {});
            onRefresh?.();
        } catch { toast.error('Erro ao atualizar status do contato'); }
    }, [contact]);

    const handleAction = useCallback(async (action: string) => {
        setShowActionMenu(null);
        if (action === 'CALL') { setShowCallForm(true); return; }
        setProcessing(true);
        try {
            await collateralApi.registerDraweeAction(analysisId, debtorDocument, action);
            setDvStatus('IN_VALIDATION');
            setHistory(h => [{ action, timestamp: new Date().toISOString() }, ...h]);
            toast.success(action === 'EMAIL' ? 'Envio de email registrado' : 'Envio de WhatsApp registrado');
            onRefresh?.();
        } catch { toast.error('Erro ao registrar ação'); }
        finally { setProcessing(false); }
    }, [analysisId, debtorDocument, onRefresh]);

    const handleValidate = useCallback(async (channel: string) => {
        setShowActionMenu(null);
        setProcessing(true);
        try {
            await collateralApi.validateDrawee(analysisId, debtorDocument, channel);
            const hasNotConfirmed = receivables.some(r => r.status === 'NOT_CONFIRMED');
            const newStatus = hasNotConfirmed ? 'PARTIALLY_VALIDATED' : 'FULLY_VALIDATED';
            setDvStatus(newStatus);
            setReceivables(prev => prev.map(r => r.status === 'PENDING' ? { ...r, status: 'CONFIRMED' } : r));
            setHistory(h => [{ action: 'VALIDATED', channel, timestamp: new Date().toISOString() }, ...h]);
            toast.success('Validação registrada');
            onRefresh?.();
        } catch { toast.error('Erro ao validar'); }
        finally { setProcessing(false); }
    }, [analysisId, debtorDocument, receivables, onRefresh]);

    const handleMarkNotConfirmed = useCallback(async (receivableId: string) => {
        if (!notConfirmObs.trim()) { toast.warn('Preencha a observação'); return; }
        setProcessing(true);
        try {
            await collateralApi.markNotConfirmed(analysisId, receivableId, notConfirmObs);
            setReceivables(prev => prev.map(r => r.id === receivableId ? { ...r, status: 'NOT_CONFIRMED', observation: notConfirmObs } : r));
            if (isValidated) setDvStatus('PARTIALLY_VALIDATED');
            setNotConfirmId(null); setNotConfirmObs('');
            onRefresh?.();
        } catch { toast.error('Erro ao marcar'); }
        finally { setProcessing(false); }
    }, [analysisId, notConfirmObs, isValidated, onRefresh]);

    const handleUnmarkNotConfirmed = useCallback(async (receivableId: string) => {
        setProcessing(true);
        try {
            await collateralApi.unmarkNotConfirmed(analysisId, receivableId);
            setReceivables(prev => prev.map(r => r.id === receivableId ? { ...r, status: isValidated ? 'CONFIRMED' : 'PENDING', observation: null } : r));
            const remaining = receivables.filter(r => r.id !== receivableId && r.status === 'NOT_CONFIRMED');
            if (isValidated && remaining.length === 0) setDvStatus('FULLY_VALIDATED');
            onRefresh?.();
        } catch { toast.error('Erro ao desmarcar'); }
        finally { setProcessing(false); }
    }, [analysisId, isValidated, receivables, onRefresh]);

    const onCallSuccess = useCallback(async (newActivity: any) => {
        setShowCallForm(false);
        const title = newActivity?.title || '';
        const body = newActivity?.body || '';
        const observation = [title, body].filter(Boolean).join('. ');
        setDvStatus(prev => prev === 'NOT_STARTED' ? 'IN_VALIDATION' : prev);
        setHistory(h => [{ action: 'CALL', timestamp: new Date().toISOString(), observation }, ...h]);
        toast.success('Ligação registrada!');
        try {
            await collateralApi.registerDraweeAction(analysisId, debtorDocument, 'CALL', observation);
        } catch { /* communication already saved */ }
        onRefresh?.();
    }, [analysisId, debtorDocument, onRefresh]);

    if (!show) return null;

    return (
        <div className="h-full flex flex-col relative">
            {processing && (
                <div className="absolute inset-0 bg-white/60 dark:bg-slate-900/60 z-50 flex items-center justify-center">
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                        Processando...
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700">
                <div className="flex items-center gap-3">
                    {contact && (
                        <Link to={`/communication/contact/${contact.id}/page-view/tab/deals`} title="Ver contato"
                            className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800">
                            <FiPhone size={16} />
                        </Link>
                    )}
                    <div className="min-w-0 overflow-hidden">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[180px]" title={debtorName}>{debtorName}</h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${STATUS_COLORS[dvStatus] || ''}`}>{STATUS_LABELS[dvStatus] || dvStatus}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-gray-500">{formatCpfCnpj(debtorDocument)}</span>
                            {contact && (
                                <div className="relative">
                                    <button onClick={() => setShowFarolMenu(!showFarolMenu)}
                                        className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] hover:bg-gray-100 dark:hover:bg-slate-700">
                                        <span className={`w-2 h-2 rounded-full ${CONTACT_STATUSES.find(s => s.value === farolStatus)?.color || 'bg-gray-400'}`} />
                                        <span className="text-gray-500">{CONTACT_STATUSES.find(s => s.value === farolStatus)?.label || 'Farol'}</span>
                                        <FiChevronDown size={9} />
                                    </button>
                                    {showFarolMenu && (
                                        <div className="absolute left-0 top-full mt-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded shadow-lg z-50 min-w-[200px]">
                                            {CONTACT_STATUSES.map(s => (
                                                <button key={s.value} onClick={() => handleFarolChange(s.value)}
                                                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2 whitespace-nowrap ${farolStatus === s.value ? 'font-medium' : ''}`}>
                                                    <span className={`w-2 h-2 rounded-full ${s.color}`} />
                                                    {s.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {!contact && !loading && (
                                <button onClick={handleCreateContact} className="text-[10px] text-blue-600 hover:underline">+ Criar contato</button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Action dropdown */}
                    {processing && (
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            Processando...
                        </div>
                    )}
                    {!isValidated && (
                        <div className="relative">
                            <button onClick={() => setShowActionMenu(showActionMenu === 'register' ? null : 'register')} disabled={loading || processing}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 rounded text-xs hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-50">
                                Registrar <FiChevronDown size={11} />
                            </button>
                            {showActionMenu === 'register' && (
                                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded shadow-lg z-50 min-w-[130px]">
                                    <button onClick={() => handleAction('CALL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiPhone size={11} /> Ligação</button>
                                    <button onClick={() => handleAction('EMAIL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiMail size={11} /> Email</button>
                                    <button onClick={() => handleAction('WHATSAPP')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FaWhatsapp size={11} /> WhatsApp</button>
                                </div>
                            )}
                        </div>
                    )}
                    {!isValidated && dvStatus !== 'NOT_STARTED' && (
                        <div className="relative">
                            <button onClick={() => setShowActionMenu(showActionMenu === 'validate' ? null : 'validate')} disabled={loading || processing}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50">
                                Validar <FiChevronDown size={11} />
                            </button>
                            {showActionMenu === 'validate' && (
                                <div className="absolute right-0 top-full mt-1 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded shadow-lg z-50 min-w-[150px]">
                                    <button onClick={() => handleValidate('EMAIL')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FiMail size={11} /> Por Email</button>
                                    <button onClick={() => handleValidate('WHATSAPP')} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-600 flex items-center gap-2"><FaWhatsapp size={11} /> Por WhatsApp</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Body - flex column: contact + summary + receivables (flex-1) + history (fixed) */}
            <div className="flex-1 flex flex-col overflow-hidden" onClick={() => { setShowActionMenu(null); setShowFarolMenu(false); }}>
                <div className="p-4 space-y-3 flex-shrink-0">
                    {/* Contact info */}
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                            Carregando contato...
                        </div>
                    )}
                    {contact && (
                        <div className="bg-gray-50 dark:bg-slate-900 rounded p-2.5 flex flex-wrap gap-3">
                            {contact.phones.map((p: any) => (
                                <div key={p.id} className="flex items-center gap-1 text-sm dark:text-white">
                                    {p.whatsapp ? <FaWhatsapp className="text-green-500" size={13} /> : <FiPhone className="text-gray-500" size={13} />}
                                    <span>{p.number}</span>
                                </div>
                            ))}
                            {contact.emails.map((e: any) => (
                                <div key={e.id} className="flex items-center gap-1 text-sm dark:text-white">
                                    <FiMail className="text-gray-500" size={13} /><span>{e.address}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Summary */}
                    <div className="grid grid-cols-2 gap-1.5 text-center">
                        <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                            <div className="text-lg font-semibold dark:text-white">{totals.count}</div>
                            <div className="text-xs text-gray-500">Recebíveis</div>
                        </div>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded p-2">
                            <div className="text-base font-semibold dark:text-white">{formatCurrency(totals.amount)}</div>
                            <div className="text-xs text-gray-500">Total</div>
                        </div>
                    </div>
                </div>

                {/* Receivables table - fills remaining space */}
                <div className="flex-1 overflow-auto px-4">
                    <ATable headers={[
                        { id: 'num', title: 'Duplicata', canSort: false, width: '80px' },
                        { id: 'parcela', title: 'Parcela', canSort: false, width: '65px' },
                        { id: 'due', title: 'Vencimento', canSort: false, width: '85px' },
                        { id: 'amt', title: 'Valor', canSort: false, cssClass: 'text-right', width: '100px' },
                        { id: 'st', title: 'Status', canSort: false, cssClass: 'text-center', width: '100px' },
                        { id: 'act', title: '', canSort: false, width: '30px' },
                    ]}>
                        {receivables.map((r: any, i: number) => (
                            <>
                                <tr key={r.id} className={`group ${i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white"
                                        title={`Cedente: ${operationCode}\nNFe: ${r.nfeKey}\nParcela: ${r.receivableNumber}`}>
                                        {r.titleNumber}
                                    </td>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white text-center">{r.receivableNumber}</td>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{formatDate(r.dueDate)}</td>
                                    <td className="py-2 px-2 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white text-right">{formatCurrency(r.amount)}</td>
                                    <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || ''}`}>{STATUS_LABELS[r.status] || r.status}</span>
                                    </td>
                                    <td className="py-1.5 px-2 border-b border-gray-200 dark:border-slate-700 text-center">
                                        {r.status === 'NOT_CONFIRMED' ? (
                                            <button onClick={() => handleUnmarkNotConfirmed(r.id)} className="opacity-0 group-hover:opacity-100 text-blue-500 hover:text-blue-600" title="Desfazer não confirmado"><HiArrowsRightLeft size={15} /></button>
                                        ) : r.status !== 'CONFIRMED' || !isValidated ? null : (
                                            <button onClick={() => { setNotConfirmId(r.id); setNotConfirmObs(''); setNotConfirmReason(''); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600" title="Marcar como não confirmado"><LuBookX size={15} /></button>
                                        )}
                                        {r.status === 'PENDING' && (
                                            <button onClick={() => { setNotConfirmId(r.id); setNotConfirmObs(''); setNotConfirmReason(''); }} className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600" title="Marcar como não confirmado"><LuBookX size={15} /></button>
                                        )}
                                    </td>
                                </tr>
                                {r.status === 'NOT_CONFIRMED' && r.observation && (
                                    <tr key={`${r.id}-obs-display`}>
                                        <td colSpan={5} className="px-2 py-1 bg-red-50/50 dark:bg-red-900/10 border-b border-gray-200 dark:border-slate-700 text-[10px] text-red-500 italic">
                                            {r.observation}
                                        </td>
                                    </tr>
                                )}
                                {notConfirmId === r.id && (
                                    <tr key={`${r.id}-obs`}>
                                        <td colSpan={5} className="px-2 py-2 bg-red-50 dark:bg-red-900/20 border-b border-gray-200 dark:border-slate-700">
                                            <div className="flex flex-col gap-2">
                                                <SelectTextField
                                                    label="Motivo"
                                                    value={notConfirmReason}
                                                    onChange={e => handleReasonChange(e.target.value)}
                                                    items={NOT_CONFIRM_REASONS.map(r => ({ value: r, label: r }))}
                                                    emptyLabel="Selecione o motivo..."
                                                />
                                                {notConfirmReason === 'Outro motivo' && (
                                                    <input type="text" value={notConfirmObs} onChange={e => setNotConfirmObs(e.target.value)} placeholder="Detalhe o motivo..."
                                                        autoFocus className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white" />
                                                )}
                                                <div className="flex gap-2 justify-end">
                                                    <button onClick={() => { setNotConfirmId(null); setNotConfirmReason(''); }} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancelar</button>
                                                    <button onClick={() => handleMarkNotConfirmed(r.id)} disabled={!notConfirmObs}
                                                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50">Salvar</button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </>
                        ))}
                    </ATable>
                </div>

                {/* History - fixed at bottom */}
                {history.length > 0 && (
                    <div className="flex-shrink-0 h-[200px] border-t border-gray-200 dark:border-slate-700 px-4 py-2">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Histórico</h4>
                        <div className="h-[160px] overflow-auto border border-gray-200 dark:border-slate-700 rounded">
                            <table className="w-full text-sm">
                                <tbody>
                                    {history.map((h: any, i: number) => (
                                        <tr key={i} className={i % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}>
                                            <td className="py-1.5 px-2 text-gray-500 whitespace-nowrap w-[140px]">{h.timestamp ? new Date(h.timestamp).toLocaleString('pt-BR') : '—'}</td>
                                            <td className="py-1.5 px-2 dark:text-white">{HISTORY_LABELS[h.action] || h.action}{h.channel ? ` (${h.channel})` : ''}</td>
                                            <td className="py-1.5 px-2 text-gray-400">{h.observation || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {showCallForm && contact && (
                <CallRegistryModalForm
                    contactId={contact.id}
                    availableDeals={deals as any}
                    preSelectedDeals={deals as any}
                    onClose={() => setShowCallForm(false)}
                    onSuccess={onCallSuccess}
                />
            )}
        </div>
    );
};

export default DraweeValidationPageForm;
